import { create } from 'zustand'
import { apiBaseUrl } from '@/logic/configuration.ts'
import authApiClient from '@/features/shared/data/axios.interceptor.ts'

interface AudioStore {
  audioContext: AudioContext
  audioBuffer: AudioBuffer | undefined
  audioSource: AudioBufferSourceNode | undefined
  isPlaying: boolean
  isPaused: boolean
  isFetching: boolean
  currentAudioId: string | undefined
  startTime: number
  pausedAt: number
  duration: number
  progress: number // Between 0 & 1
  animationFrameId: number | undefined
  playAudioBuffer: (
    id: string,
    buffer: AudioBuffer,
    progress?: number,
    startTimeInSeconds?: number
  ) => void
  playAudioUrl: (
    id: string,
    url: string,
    progress?: number,
    startTimeInSeconds?: number
  ) => Promise<void>
  pausePlayback: () => void
  stopPlayback: () => void
  setProgress: (newProgress: number) => void
}

const RESET_STATE: Partial<AudioStore> = {
  audioSource: undefined,
  isPlaying: false,
  pausedAt: 0,
  currentAudioId: undefined,
  audioBuffer: undefined,
  duration: 0,
  progress: 0,
}

const useAudioStore = create<AudioStore>((set, get) => {
  const updateProgress = () => {
    const state = get()

    if (state.isPlaying && state.duration > 0) {
      const elapsed = state.audioContext.currentTime - state.startTime
      let newProgress = elapsed / state.duration

      if (newProgress > 1) {
        newProgress = 1
      }
      set({ progress: newProgress })

      if (state.isPlaying && newProgress < 1) {
        const frameId = requestAnimationFrame(updateProgress)
        set({ animationFrameId: frameId })
      }
    }
  }

  const cancelProgressUpdater = () => {
    const { animationFrameId } = get()
    if (animationFrameId !== undefined) {
      cancelAnimationFrame(animationFrameId)
      set({ animationFrameId: undefined })
    }
  }

  const onEnded = () => {
    cancelProgressUpdater()
    // Reset if the audio has finished playing
    if (!get().isPaused) {
      set({
        ...RESET_STATE,
        startTime: 0,
      })
    } else {
      // Resume if the audio was paused
      set({
        isPlaying: false,
        audioSource: undefined,
      })
    }
  }

  const createAndStartSource = (buffer: AudioBuffer, offset: number) => {
    const state = get()
    const source = state.audioContext.createBufferSource()
    source.buffer = buffer
    source.connect(state.audioContext.destination)
    source.onended = onEnded
    if (state.audioContext.state === 'suspended') {
      state.audioContext.resume()
    }
    source.start(0, offset)
    return source
  }

  return {
    audioContext: new AudioContext(),
    audioBuffer: undefined,
    audioSource: undefined,
    isPlaying: false,
    isPaused: false,
    isFetching: false,
    currentAudioId: undefined,
    startTime: 0,
    pausedAt: 0,
    duration: 0,
    progress: 0,
    animationFrameId: undefined,

    playAudioBuffer: (id, buffer, progress, startTimeInSeconds) => {
      const state = get()
      // The current audio is stopped, if any
      if (state.audioSource) {
        state.audioSource.onended = null
        state.audioSource.stop()
      }
      set({ audioBuffer: buffer, duration: buffer.duration })
      const offset =
        typeof startTimeInSeconds === 'number'
          ? startTimeInSeconds
          : typeof progress === 'number'
            ? progress * buffer.duration
            : state.currentAudioId === id && state.pausedAt > 0
              ? state.pausedAt
              : 0

      cancelProgressUpdater()
      const source = createAndStartSource(buffer, offset)
      set({
        audioSource: source,
        isPlaying: true,
        isPaused: false,
        currentAudioId: id,
        startTime: state.audioContext.currentTime - offset,
        pausedAt: 0,
      })
      updateProgress()
    },

    playAudioUrl: async (id, url, progress, startTimeInSeconds) => {
      const state = get()
      // Any previous audio is stopped
      if (state.currentAudioId && state.currentAudioId !== id) {
        get().stopPlayback()
      }
      set({ currentAudioId: id, isFetching: true })
      try {
        const isAuthResource = url.includes(apiBaseUrl)
        let arrayBuffer: ArrayBuffer

        if (isAuthResource) {
          const response = await authApiClient.get<ArrayBuffer>(url, {
            responseType: 'arraybuffer',
          })
          arrayBuffer = response.data
        } else {
          const response = await fetch(url)
          arrayBuffer = await response.arrayBuffer()
        }

        const audioBuffer =
          await state.audioContext.decodeAudioData(arrayBuffer)
        set({ isFetching: false })
        get().playAudioBuffer(id, audioBuffer, progress, startTimeInSeconds)
      } catch (error) {
        console.error('Error loading audio from url', error)
        set({ isFetching: false })
      }
    },

    pausePlayback: () => {
      const state = get()
      if (state.audioSource && state.isPlaying) {
        const elapsed = state.audioContext.currentTime - state.startTime
        set({ isPaused: true, pausedAt: elapsed, isPlaying: false })
        // This is to avoid the onended event to be triggered, cleaning the state
        state.audioSource.onended = null
        state.audioSource.stop()
        cancelProgressUpdater()
      }
    },

    stopPlayback: () => {
      const state = get()
      if (state.audioSource) {
        set({ isPaused: false })
        state.audioSource.onended = null
        state.audioSource.stop()
        cancelProgressUpdater()
        set(RESET_STATE)
      }
    },

    setProgress: (newProgress: number) => {
      const state = get()
      const { audioBuffer, duration, currentAudioId } = state

      // When there's nothing being played, the progress is just updated
      set({ progress: newProgress })
      if (!audioBuffer || duration <= 0 || !currentAudioId) {
        return
      }

      const newOffset = newProgress * duration
      if (state.isPlaying) {
        if (state.audioSource) {
          state.audioSource.onended = null
          state.audioSource.stop()
        }
        const source = createAndStartSource(audioBuffer, newOffset)
        set({
          audioSource: source,
          startTime: state.audioContext.currentTime - newOffset,
          pausedAt: 0,
          isPaused: false,
        })
        cancelProgressUpdater()
        updateProgress()
      } else if (state.isPaused) {
        set({ pausedAt: newOffset })
      }
    },
  }
})

export default useAudioStore
